import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiDelete, apiGet, apiPost, apiPut } from "../../shared/apiClient";
import { useToast } from "../../shared/ToastContext";

const empty = { name: "", mobileNo: "", email: "", address: "", website: "", loginUserName: "", loginPassword: "" };
const emptyReset = { userName: "", password: "" };

function initials(name) {
  if (!name || !String(name).trim()) return "?";
  const p = String(name).trim().split(/\s+/);
  return (p[0][0] + (p[1]?.[0] || "")).toUpperCase();
}

function PlusIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PencilIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function TrashIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function KeyIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="7.5" cy="15.5" r="3.5" />
      <path d="M11 13l8-8" />
      <path d="M16 5h3v3" />
    </svg>
  );
}

const fieldConfig = [
  { key: "name", label: "Company name", required: true, section: "Identity" },
  { key: "email", label: "Email", type: "email", section: "Contact" },
  { key: "mobileNo", label: "Mobile", section: "Contact" },
  { key: "website", label: "Website", section: "Contact" },
  { key: "address", label: "Address", multiline: true, section: "Location" },
  { key: "loginUserName", label: "Login username", required: true, section: "Portal Access", createOnly: true },
  { key: "loginPassword", label: "Login password", required: true, type: "password", section: "Portal Access", createOnly: true }
];

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15";

export function CustomersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState(emptyReset);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    const q = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : "";
    const res = await apiGet(`/api/Customers${q}`);
    if (res.ok) setRows(res.data ?? []);
    else toast.push(String(res.error), "error");
    setLoading(false);
  }, [isAdmin, search, toast]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingId(null);
    setForm(empty);
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [modalOpen, closeModal]);

  useEffect(() => {
    if (!deleteTarget) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDeleteTarget(null);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [deleteTarget]);

  if (!isAdmin) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">
        You do not have access to this section.
      </div>
    );
  }

  const openAddModal = () => {
    setEditingId(null);
    setForm(empty);
    setModalOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return;
    if (!editingId && !form.loginUserName?.trim()) {
      toast.push("Login username is required", "error");
      return;
    }
    if (!editingId && !form.loginPassword?.trim()) {
      toast.push("Login password is required", "error");
      return;
    }
    setSaving(true);
    if (editingId) {
      const res = await apiPut(`/api/Customers/${editingId}`, {
        id: editingId,
        name: form.name,
        mobileNo: form.mobileNo || null,
        email: form.email || null,
        address: form.address || null,
        website: form.website || null
      });
      if (!res.ok) toast.push(String(res.error), "error");
      else {
        toast.push("Customer updated", "success");
        await load();
        closeModal();
      }
    } else {
      const res = await apiPost("/api/Customers", {
        name: form.name,
        mobileNo: form.mobileNo || null,
        email: form.email || null,
        address: form.address || null,
        website: form.website || null,
        loginUserName: form.loginUserName,
        loginPassword: form.loginPassword
      });
      if (!res.ok) toast.push(String(res.error), "error");
      else {
        toast.push("Customer created", "success");
        await load();
        closeModal();
      }
    }
    setSaving(false);
  };

  const onEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name ?? "",
      mobileNo: r.mobileNo ?? "",
      email: r.email ?? "",
      address: r.address ?? "",
      website: r.website ?? ""
    });
    setModalOpen(true);
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const res = await apiDelete(`/api/Customers/${deleteTarget.id}`);
    setDeleting(false);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    toast.push("Deleted", "success");
    await load();
    setDeleteTarget(null);
  };

  const openResetModal = (r) => {
    setResetTarget({ id: r.id, name: r.name || "Customer" });
    setResetForm({ userName: r.loginUserName || "", password: "" });
  };

  const runResetUser = async () => {
    if (!resetTarget) return;
    if (!resetForm.userName.trim()) {
      toast.push("Username is required", "error");
      return;
    }
    if (!resetForm.password.trim()) {
      toast.push("Password is required", "error");
      return;
    }

    setResetting(true);
    const res = await apiPost(`/api/Customers/${resetTarget.id}/reset-user`, {
      userName: resetForm.userName,
      password: resetForm.password
    });
    setResetting(false);

    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }

    toast.push("Customer login reset", "success");
    await load();
    setResetTarget(null);
    setResetForm(emptyReset);
  };

  const sections = [...new Set(fieldConfig.map((f) => f.section))];

  return (
    <div className="w-full space-y-4">
      {/* Card: heading + search + add */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Customers</h1>
        <p className="mt-1 text-sm text-slate-500">Manage company accounts and contact details</p>
        <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden>
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
            </span>
            <input
              type="search"
              className={`${inputClass} pl-9`}
              placeholder="Search name, email, code, mobile…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search customers"
            />
          </div>
          <div className="flex shrink-0 items-center justify-end gap-3">
            <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
              {rows.length} {rows.length === 1 ? "record" : "records"}
            </div>
            <button
              type="button"
              onClick={openAddModal}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-800 text-white shadow-md transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              title="Add customer"
              aria-label="Add customer"
            >
              <PlusIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Card: table only */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 sm:px-5">
          <h2 className="text-sm font-semibold text-slate-800">All customers</h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <span
              className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600"
              aria-hidden
            />
            Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="bg-white">
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Company</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Email</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Username</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Address</th>
                  <th className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-500">Code</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="bg-white transition hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <div className="flex max-w-xs items-center gap-3">
                        <div
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-b from-brand-600 to-brand-800 text-xs font-bold text-white"
                          aria-hidden
                        >
                          {initials(r.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">{r.name || "—"}</p>
                          {r.mobileNo && <p className="truncate text-xs text-slate-500">{r.mobileNo}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.loginUserName || "—"}</td>
                    <td className="max-w-[260px] px-4 py-3 text-slate-600">
                      <span className="block truncate" title={r.address || ""}>
                        {r.address || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.code ? (
                        <span className="inline-block rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-700">
                          {r.code}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center justify-end gap-0.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/customers/${r.id}/employees`)}
                          className="inline-flex h-8 items-center justify-center rounded-lg px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                          title="Employees"
                          aria-label={`Manage employees for ${r.name || "customer"}`}
                        >
                          Employees
                        </button>
                        <button
                          type="button"
                          onClick={() => openResetModal(r)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-700 transition hover:bg-slate-100"
                          title="Reset customer login"
                          aria-label={`Reset login for ${r.name || "customer"}`}
                        >
                          <KeyIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => onEdit(r)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-brand-800 transition hover:bg-emerald-50"
                          title="Edit"
                          aria-label={`Edit ${r.name || "customer"}`}
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget({ id: r.id, name: r.name || "Customer" })}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50"
                          title="Delete"
                          aria-label={`Delete ${r.name || "customer"}`}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                      No customers found. Use the + button to add one or adjust your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setDeleteTarget(null)}
            aria-label="Close"
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600" aria-hidden>
              <TrashIcon className="h-5 w-5" />
            </div>
            <h2 id="delete-dialog-title" className="mt-3 text-lg font-semibold text-slate-900">
              Delete customer?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              This will remove <span className="font-medium text-slate-800">{deleteTarget.name}</span> permanently. This
              cannot be undone.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runDelete()}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setResetTarget(null)}
            aria-label="Close"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-dialog-title"
            className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700" aria-hidden>
              <KeyIcon className="h-5 w-5" />
            </div>
            <h2 id="reset-dialog-title" className="mt-3 text-lg font-semibold text-slate-900">
              Reset customer login
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Update username and password for <span className="font-medium text-slate-800">{resetTarget.name}</span>.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Username *</label>
                <input
                  type="text"
                  className={inputClass}
                  value={resetForm.userName}
                  onChange={(e) => setResetForm((prev) => ({ ...prev, userName: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Password *</label>
                <input
                  type="password"
                  className={inputClass}
                  value={resetForm.password}
                  onChange={(e) => setResetForm((prev) => ({ ...prev, password: e.target.value }))}
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runResetUser()}
                disabled={resetting}
                className="rounded-lg bg-brand-800 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-60"
              >
                {resetting ? "Updating…" : "Update login"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center sm:p-6"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={closeModal}
            aria-label="Close dialog"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="customer-modal-title"
            className="relative z-10 flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <h2 id="customer-modal-title" className="text-lg font-semibold text-slate-900">
                  {editingId ? "Edit customer" : "Add customer"}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {editingId ? "Update the fields below, then save." : "Company name is required."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={onSave} className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
              {sections.map((section) => (
                <div key={section} className="mb-5 last:mb-0">
                  <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{section}</h3>
                  <div
                    className={section === "Location" ? "space-y-3" : "grid grid-cols-1 gap-3 sm:grid-cols-2"}
                  >
                    {fieldConfig
                      .filter((f) => f.section === section)
                      .filter((f) => !(editingId && f.createOnly))
                      .map((f) => (
                        <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                          <label className="mb-1.5 block text-xs font-medium text-slate-600">
                            {f.label}
                            {f.required && <span className="text-red-500"> *</span>}
                          </label>
                          {f.multiline ? (
                            <textarea
                              className={inputClass}
                              rows={3}
                              value={form[f.key]}
                              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                            />
                          ) : (
                            <input
                              type={f.type || "text"}
                              className={inputClass}
                              value={form[f.key]}
                              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              <div className="mt-1 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-brand-800 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Saving…" : editingId ? "Save changes" : "Create customer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
